import React from "react";
import styled from "styled-components";
import { NumberStepper } from "../NumberStepper";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import {
  Field,
  FieldLabel,
  HelperText,
} from "../shared/scheduleField.styles";

export interface RepeatEveryFieldProps {
  value: number;
  /** Static unit word beside the stepper — "Week" or "Month". */
  unitWord: string;
  onChange: (next: number) => void;
  error?: string;
  showError?: boolean;
  className?: string;
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  width: 100%;
`;

/** Figma sizes this schedule-refresh stepper at 104px. */
const StepperSlot = styled.div`
  width: calc(${spacing[8]} + ${spacing[8]} + ${spacing[8]} + ${spacing[2]});
  flex: 0 0 auto;
`;

const UnitWord = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * "Repeat every N Week/Month" — stepper plus a static unit word.
 *
 * The unit is *not* a dropdown here: it is fixed by the `Repeat` preset, so
 * offering `UNIT_OPTIONS` would let the user contradict their own selection.
 * That is the one place this diverges from the connector `SchedulePicker`.
 */
export const RepeatEveryField: React.FC<RepeatEveryFieldProps> = ({
  value,
  unitWord,
  onChange,
  error,
  showError = false,
  className,
}) => {
  const visibleError = showError && error ? error : undefined;

  return (
    <Field className={className}>
      <FieldLabel>Repeat every</FieldLabel>
      <Row>
        <StepperSlot>
          <NumberStepper
            value={value}
            min={1}
            max={99}
            invalid={Boolean(visibleError)}
            ariaLabel="Repeat interval"
            onChange={onChange}
          />
        </StepperSlot>
        <UnitWord>{unitWord}</UnitWord>
      </Row>
      {visibleError && <HelperText $error>{visibleError}</HelperText>}
    </Field>
  );
};

export default RepeatEveryField;
